/**
 * Video Swipe Reels
 * Tinder/TikTok-style infinite card stack for Shopify OS 2.0
 * No jQuery, no third-party carousel deps. Pointer Events + CSS custom props.
 *
 * Loop mechanism: fixed set of DOM card nodes (<= 10). "Infinite" loop is
 * achieved by reassigning each card's data-position (active/prev/next/hidden)
 * via modulo index math on swipe — no nodes are ever cloned, added, or removed.
 */

class VideoSwipeReels extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.cardCount = 0;
    this.activeIndex = 0;
    this.layoutMode = 'horizontal';
    this.autoplayEnabled = true;

    this.pointerState = null; // holds active drag data or null
    this.SWIPE_THRESHOLD_RATIO = 0.28; // fraction of card width/height to trigger a swipe
    this.VELOCITY_THRESHOLD = 0.5; // px/ms — fast flick counts even under distance threshold

    this.io = null;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.stackEl = this.querySelector('.vsr-stack');
    if (!this.stackEl) return;

    this.cards = Array.from(this.stackEl.querySelectorAll('.vsr-card'));
    this.cardCount = this.cards.length;
    if (this.cardCount === 0) return;

    this.layoutMode = this.dataset.layoutMode === 'vertical' ? 'vertical' : 'horizontal';
    this.autoplayEnabled = this.dataset.autoplay !== 'false';

    this.cards.forEach((card, i) => {
      card.addEventListener('pointerdown', (e) => this.onPointerDown(e, card, i));
      card.addEventListener('click', (e) => this.onCardClick(e, card, i));

      const soundBtn = card.querySelector('[data-sound-toggle]');
      if (soundBtn) {
        soundBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleSound(card);
        });
      }
    });

    this.addEventListener('keydown', this.onKeyDown);

    this.setupIntersectionObserver();
    this.renderPositions();
    this.loadCardVideo(this.cards[this.activeIndex]);
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }

  /* ---------------------------------------------------------------------
   * Position assignment — the core of the loop. Given activeIndex, work
   * out which physical card node is "prev", "active", "next", the rest
   * are "hidden". Modulo arithmetic makes this wrap seamlessly.
   * ------------------------------------------------------------------- */

  mod(n, m) {
    return ((n % m) + m) % m;
  }

  renderPositions() {
    const prevIdx = this.mod(this.activeIndex - 1, this.cardCount);
    const nextIdx = this.mod(this.activeIndex + 1, this.cardCount);

    this.cards.forEach((card, i) => {
      card.classList.remove('vsr-active', 'vsr-exit-left', 'vsr-exit-right', 'vsr-exit-up', 'vsr-exit-down');
      if (i === this.activeIndex) {
        card.dataset.position = 'active';
        card.classList.add('vsr-active');
      } else if (i === prevIdx) {
        card.dataset.position = 'prev';
      } else if (i === nextIdx) {
        card.dataset.position = 'next';
      } else {
        card.dataset.position = 'hidden';
      }
      card.style.removeProperty('--vsr-x');
      card.style.removeProperty('--vsr-y');
      card.style.removeProperty('--vsr-rotate');
      card.style.removeProperty('--vsr-opacity');
    });

    this.handleAutoplayForActiveCard();
    this.preloadNeighbors(prevIdx, nextIdx);
  }

  /* ---------------------------------------------------------------------
   * Pointer drag handling
   * ------------------------------------------------------------------- */

  onPointerDown(e, card, index) {
    if (index !== this.activeIndex) return; // only the active card is draggable
    if (e.target.closest('[data-sound-toggle]')) return;

    card.setPointerCapture(e.pointerId);
    card.classList.add('vsr-dragging');

    this.pointerState = {
      pointerId: e.pointerId,
      card,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
      moved: false,
    };

    card.addEventListener('pointermove', this.onPointerMove);
    card.addEventListener('pointerup', this.onPointerUp);
    card.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(e) {
    const ps = this.pointerState;
    if (!ps || e.pointerId !== ps.pointerId) return;

    const dx = e.clientX - ps.startX;
    const dy = e.clientY - ps.startY;

    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) ps.moved = true;

    const now = performance.now();
    const dt = Math.max(1, now - ps.lastT);
    if (this.layoutMode === 'horizontal') {
      ps.velocity = (e.clientX - ps.lastX) / dt;
    } else {
      ps.velocity = (e.clientY - ps.lastY) / dt;
    }
    ps.lastX = e.clientX;
    ps.lastY = e.clientY;
    ps.lastT = now;

    if (this.layoutMode === 'horizontal') {
      const rotate = Math.max(-18, Math.min(18, dx / 12));
      ps.card.style.setProperty('--vsr-x', `${dx}px`);
      ps.card.style.setProperty('--vsr-y', `${dy * 0.15}px`);
      ps.card.style.setProperty('--vsr-rotate', `${rotate}deg`);
    } else {
      ps.card.style.setProperty('--vsr-x', `${dx * 0.15}px`);
      ps.card.style.setProperty('--vsr-y', `${dy}px`);
    }

    const dragDistance = this.layoutMode === 'horizontal' ? Math.abs(dx) : Math.abs(dy);
    const fadeOpacity = Math.max(0.4, 1 - dragDistance / 500);
    ps.card.style.setProperty('--vsr-opacity', fadeOpacity.toFixed(2));
  }

  onPointerUp(e) {
    const ps = this.pointerState;
    if (!ps || e.pointerId !== ps.pointerId) return;

    ps.card.removeEventListener('pointermove', this.onPointerMove);
    ps.card.removeEventListener('pointerup', this.onPointerUp);
    ps.card.removeEventListener('pointercancel', this.onPointerUp);
    ps.card.classList.remove('vsr-dragging');

    const dx = ps.lastX - ps.startX;
    const dy = ps.lastY - ps.startY;
    const cardRect = ps.card.getBoundingClientRect();

    let swiped = false;
    let direction = null;

    if (this.layoutMode === 'horizontal') {
      const thresholdPx = cardRect.width * this.SWIPE_THRESHOLD_RATIO;
      if (Math.abs(dx) > thresholdPx || Math.abs(ps.velocity) > this.VELOCITY_THRESHOLD) {
        swiped = true;
        direction = dx > 0 || ps.velocity > 0 ? 'right' : 'left';
      }
    } else {
      const thresholdPx = cardRect.height * this.SWIPE_THRESHOLD_RATIO;
      if (Math.abs(dy) > thresholdPx || Math.abs(ps.velocity) > this.VELOCITY_THRESHOLD) {
        swiped = true;
        direction = dy > 0 || ps.velocity > 0 ? 'down' : 'up';
      }
    }

    if (swiped) {
      this.commitSwipe(ps.card, direction);
    } else {
      // spring back — clear inline overrides, CSS transition handles the return
      ps.card.style.removeProperty('--vsr-x');
      ps.card.style.removeProperty('--vsr-y');
      ps.card.style.removeProperty('--vsr-rotate');
      ps.card.style.removeProperty('--vsr-opacity');
    }

    ps.wasSwipe = ps.moved;
    this.pointerState = null;
  }

  commitSwipe(card, direction) {
    card.style.removeProperty('--vsr-x');
    card.style.removeProperty('--vsr-y');
    card.style.removeProperty('--vsr-rotate');
    card.style.removeProperty('--vsr-opacity');
    card.classList.add(`vsr-exit-${direction}`);

    const advanceForward = direction === 'right' || direction === 'down';

    const onExitDone = () => {
      card.removeEventListener('transitionend', onExitDone);
      this.activeIndex = this.mod(this.activeIndex + (advanceForward ? 1 : -1), this.cardCount);
      this.renderPositions();
    };
    card.addEventListener('transitionend', onExitDone, { once: true });
  }

  /* ---------------------------------------------------------------------
   * Click-to-open-product — distinguishes a genuine tap from a drag that
   * sprang back without crossing the swipe threshold.
   * ------------------------------------------------------------------- */

  onCardClick(e, card, index) {
    if (index !== this.activeIndex) {
      // clicking a visible neighbor card navigates the stack to it instead
      // of opening a product, matching Tinder-stack UX expectations
      e.preventDefault();
      this.activeIndex = index;
      this.renderPositions();
      return;
    }
    if (e.target.closest('[data-sound-toggle]')) return;

    // if the pointerup handler detected real movement, this click is a
    // drag-release artifact, not an intentional tap — ignore it
    if (this.pointerState && this.pointerState.moved) return;

    const url = card.dataset.productUrl;
    if (url) window.location.href = url;
  }

  onKeyDown(e) {
    if (e.key === 'ArrowRight' || (this.layoutMode === 'vertical' && e.key === 'ArrowDown')) {
      e.preventDefault();
      this.activeIndex = this.mod(this.activeIndex + 1, this.cardCount);
      this.renderPositions();
    } else if (e.key === 'ArrowLeft' || (this.layoutMode === 'vertical' && e.key === 'ArrowUp')) {
      e.preventDefault();
      this.activeIndex = this.mod(this.activeIndex - 1, this.cardCount);
      this.renderPositions();
    } else if (e.key === 'Enter') {
      const activeCard = this.cards[this.activeIndex];
      const url = activeCard && activeCard.dataset.productUrl;
      if (url) window.location.href = url;
    }
  }

  /* ---------------------------------------------------------------------
   * Video lazy-load, autoplay-active-only, sound toggle
   * ------------------------------------------------------------------- */

  loadCardVideo(card) {
    if (!card) return;
    const video = card.querySelector('.vsr-video');
    if (!video || video.dataset.loaded === 'true') return;

    const sources = video.querySelectorAll('source[data-src]');
    if (sources.length === 0) return;

    sources.forEach((source) => {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
    });
    video.load();
    video.dataset.loaded = 'true';
  }

  preloadNeighbors(prevIdx, nextIdx) {
    this.loadCardVideo(this.cards[this.activeIndex]);
    this.loadCardVideo(this.cards[prevIdx]);
    this.loadCardVideo(this.cards[nextIdx]);
  }

  handleAutoplayForActiveCard() {
    this.cards.forEach((card, i) => {
      const video = card.querySelector('.vsr-video');
      if (!video) return;
      if (i === this.activeIndex && this.autoplayEnabled) {
        // actual play() call is gated by IntersectionObserver below to
        // respect "pause when off-screen"; here we just mark intent
        video.dataset.wantsPlay = 'true';
        this.tryPlay(video);
      } else {
        video.dataset.wantsPlay = 'false';
        video.pause();
      }
    });
  }

  tryPlay(video) {
    if (!this.isInViewport(video)) return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        /* autoplay blocked (rare, since muted) — no-op, sound toggle still works */
      });
    }
  }

  isInViewport(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  setupIntersectionObserver() {
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const card = video.closest('.vsr-card');
          const idx = this.cards.indexOf(card);
          const isActiveCard = idx === this.activeIndex;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            if (isActiveCard && this.autoplayEnabled) this.tryPlay(video);
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.6, 1] }
    );

    this.cards.forEach((card) => {
      const video = card.querySelector('.vsr-video');
      if (video) this.io.observe(video);
    });
  }

  toggleSound(card) {
    const video = card.querySelector('.vsr-video');
    const btn = card.querySelector('[data-sound-toggle]');
    if (!video || !btn) return;

    video.muted = !video.muted;
    btn.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
  }
}

if (!customElements.get('video-swipe-reels')) {
  customElements.define('video-swipe-reels', VideoSwipeReels);
}
